import { useState } from "react";
import useAuth from "../../hooks/useAuth";
import { Link } from "react-router-dom";
import { supabase } from "../../supabase";
import { validateNickname } from "../../utils/validateNickname";
import { useTranslation } from "react-i18next";
import AuthSplit from "../../components/AuthSplit";

export default function RegisterPage() {
  const { signup } = useAuth();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // 👁 비밀번호 보기
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  // 완료 모달
  const [signupDone, setSignupDone] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");

  // 🔒 제출 중 상태 (추가)
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (isSubmitting) return; // 🔒 연타 방지

    setErrorMsg("");
    setIsSubmitting(true);

    // ⬅️ 이메일 체크
    if (!email) {
      setErrorMsg(t("auth_page.enter_email"));
      setIsSubmitting(false);
      return;
    }

    // ⬅️ 닉네임 체크
    const trimmedUsername = username.trim();
    const nicknameValidationError = validateNickname(trimmedUsername);

    if (nicknameValidationError) {
      setErrorMsg(nicknameValidationError);
      setIsSubmitting(false);
      return;
    }

    const { data: existingUsername, error: usernameCheckError } = await supabase
      .from("profiles")
      .select("id")
      .eq("username", trimmedUsername)
      .maybeSingle();

    if (usernameCheckError) {
      setErrorMsg(t("auth_page.nickname_check_failed"));
      setIsSubmitting(false);
      return;
    }

    if (existingUsername) {
      setErrorMsg(t("auth_page.nickname_taken"));
      setIsSubmitting(false);
      return;
    }

    // ⬅️ 비밀번호 체크
    if (!pw) {
      setErrorMsg(t("auth_page.enter_password"));
      setIsSubmitting(false);
      return;
    }

    // ⬅️ 비밀번호 확인 체크
    if (!pw2) {
      setErrorMsg(t("auth_page.enter_password_confirm"));
      setIsSubmitting(false);
      return;
    }

    // ⬅️ 기존 코드
    if (pw !== pw2) {
      setErrorMsg(t("auth_page.password_mismatch"));
      setIsSubmitting(false);
      return;
    }

    const { error } = await signup(email, pw, trimmedUsername);

    // ⬅️ Supabase 에러
    if (error) {
      if (error.message === "프로필 저장에 실패했습니다.") {
        setErrorMsg(t("auth_page.profile_save_failed"));
      } else {
        setErrorMsg(t("auth_page.signup_failed"));
      }
      setIsSubmitting(false);
      return;
    }

    setSignupEmail(email);
    setSignupDone(true);
    setIsSubmitting(false);
  }

  const EyeIcon = ({ open }) =>
    open ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3l18 18" />
        <path d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 1.42-.42" />
        <path d="M9.88 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.6 18.6 0 0 1-3.17 4.57" />
        <path d="M6.61 6.61A18.6 18.6 0 0 0 1 12s4 8 11 8a10.94 10.94 0 0 0 4.76-1.06" />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );

  return (
    <>
      <AuthSplit
        hero1={t("auth_split.hero1")}
        hero2={t("auth_split.hero2")}
        lead={t("auth_split.register_lead")}
        benefits
        title={t("register")}
        sub={t("auth_split.register_sub")}
      >
        {errorMsg && <p className="auth-error-text">{errorMsg}</p>}

        <form className="auth-form" onSubmit={onSubmit}>
          <div className="ca-field">
            <label className="ca-label" htmlFor="reg-nick">{t("auth_page.nickname")}</label>
            <input
              id="reg-nick"
              className="auth-input"
              type="text"
              placeholder={t("auth_split.nickname_placeholder")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              minLength={2}
              maxLength={20}
              required
            />
          </div>

          <div className="ca-field">
            <label className="ca-label" htmlFor="reg-email">{t("email")}</label>
            <input
              id="reg-email"
              className="auth-input"
              type="email"
              autoComplete="email"
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="ca-field">
            <label className="ca-label" htmlFor="reg-pw">{t("password")}</label>
            <div className="password-wrapper">
              <input
                id="reg-pw"
                className="auth-input"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("auth_split.pw_placeholder")}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPw((v) => !v)}
                aria-label="비밀번호 보기"
              >
                <EyeIcon open={showPw} />
              </button>
            </div>
          </div>

          <div className="ca-field">
            <label className="ca-label" htmlFor="reg-pw2">{t("auth_page.password_confirm")}</label>
            <div className="password-wrapper">
              <input
                id="reg-pw2"
                className="auth-input"
                type={showPw2 ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("auth_split.retype")}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPw2((v) => !v)}
                aria-label="비밀번호 보기"
              >
                <EyeIcon open={showPw2} />
              </button>
            </div>
          </div>

          <button
            className="auth-btn"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? t("auth_page.processing") : t("register")}
          </button>
        </form>

        <div className="auth-links">
          <span>{t("auth_split.have_account")}</span>
          <Link to="/login">{t("login")}</Link>
        </div>
      </AuthSplit>

      {/* 회원가입 완료 모달 */ }
  {
    signupDone && (
      <div className="modal-overlay">
        <div className="modal">
          <h3>{t("auth_page.signup_complete")}</h3>
          <p style={{ marginTop: 12, lineHeight: 1.5 }}>
            {t("auth_page.verification_sent", { email: signupEmail })}<br />
            {t("auth_page.click_verification_then_login")}
          </p>
          <div className="modal-actions">
            <button
              onClick={() => {
                setSignupDone(false);
                window.location.hash = "/login";
              }}
            >
              {t("auth_page.go_to_login_page")}
            </button>
          </div>
        </div>
      </div>
    )
  }
    </>
  );
}
